import React, { useCallback, useMemo, useState } from 'react';
import { useFormikContext, type FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { FormikWrapper } from '@uncinc/uncinc-react-kitchen-sink';

import { Button, StepperField } from '../../components';
import Modal from '../../containers/Modal';
import { useModalContext } from '../../containers/Modal/Context';
import formatDate from '../../helpers/formatDate';
import useClasses from '../../hooks/useClasses';
import useDailyRegistrations from '../../hooks/useDailyRegistrations';
import useRoutes from '../../hooks/useRoutes';
import useToast from '../../hooks/useToast';
import useTypedDispatch from '../../hooks/useTypedDispatch';
import { reinitializeRegistrationData } from '../../store/actions/Registrations';
import { webformSubmit } from '../../store/actions/Webform';
import useValidationSchema from './validationSchema';
import {
  DOMAIN_ZELFHULP_GOKKEN,
  USAGE_STEP_INCREMENT,
  ZELFHULP_GOKKEN_USAGE_STEP_INCREMENT,
  ZELFHULP_GOKKEN_MAX_USAGE,
  PROGRAM_DURATION_WEEKS,
} from '../../config/constants';

import './style.scss';

interface FormValues {
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
}

export interface ChangeRecoveryPlanFormInnerProps {
  initialValues: FormValues;
}

const ChangeRecoveryPlanFormInner: React.FC<ChangeRecoveryPlanFormInnerProps> = (props) => {
  const { initialValues } = props;

  const { t } = useTranslation();
  const { isSubmitting } = useFormikContext<FormValues>();

  const { buildClass } = useClasses('PageChangeRecoveryPlan');
  const { actionsRef } = useModalContext();

  const currentDomain = useSelector<ImmutableRootState<string>, string>(
    (state) => state.getIn(['uiInternal', 'currentDomain', 'raw'], null),
  );

  const stepIncrement = currentDomain === DOMAIN_ZELFHULP_GOKKEN
    ? Number(ZELFHULP_GOKKEN_USAGE_STEP_INCREMENT)
    : Number(USAGE_STEP_INCREMENT);

  const [fieldsWithIncreasedGoal, setFieldsWithIncreasedGoal] = useState<(keyof FormValues)[]>([]);

  const handleValueChange = useCallback((
    dayOfWeek: keyof FormValues,
    newValue: string,
  ): void => {
    const hasIncreasedGoal = Number(newValue) > Number(initialValues[dayOfWeek]);

    setFieldsWithIncreasedGoal((prev) => {
      if (hasIncreasedGoal) {
        return prev.includes(dayOfWeek) ? prev : [...prev, dayOfWeek];
      }
      return prev.filter((field) => field !== dayOfWeek);
    });
  }, [initialValues]);

  return (
    <>
      {Object.keys(initialValues).map((dayOfWeek: keyof FormValues) => (
        <StepperField
          key={`stepper-day-of-week-${dayOfWeek}`}
          fieldName={dayOfWeek}
          title={t(`dates.${dayOfWeek}`)}
          value={Number(initialValues[dayOfWeek])}
          showCurrentGoal
          unitName={t(`pages.create_recovery_plan.units.${currentDomain}`)}
          stepIncrement={stepIncrement}
          max={currentDomain === DOMAIN_ZELFHULP_GOKKEN && Number(ZELFHULP_GOKKEN_MAX_USAGE)}
          customError={fieldsWithIncreasedGoal.includes(dayOfWeek) && t('pages.change_recovery_plan.error')}
          onValueChange={(newValue) => handleValueChange(dayOfWeek, newValue)}
        />
      ))}

      <div className={buildClass('actions')} ref={actionsRef}>
        <Button
          htmlType="submit"
          className={buildClass('submit')}
          buttonState={isSubmitting ? 'disabled' : ''}
        >
          {t('buttons.save')}
        </Button>
      </div>
    </>
  );
};

const ChangeRecoveryPlanForm: React.FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'pages.change_recovery_plan' });
  const history = useHistory();
  const location = useLocation();
  const dispatch = useTypedDispatch();

  const { recoveryPlan } = useRoutes();
  const toast = useToast();
  const validationSchema = useValidationSchema();

  const { currentWeekNumber, getRegistrationsByWeekNumber } = useDailyRegistrations();

  const weekNumber = useMemo((): number | null => {
    const queryParams = new URLSearchParams(location.search);
    const weekNumberFromUrl = queryParams.get('week');
    return weekNumberFromUrl ? parseInt(weekNumberFromUrl, 10) : null;
  }, [location.search]);

  const isWithinProgramDuration = useMemo((): boolean => (
    weekNumber && weekNumber > currentWeekNumber && weekNumber <= PROGRAM_DURATION_WEEKS
  ), [currentWeekNumber, weekNumber]);

  const initialValues = useMemo((): FormValues => {
    const values: FormValues = {
      monday: '',
      tuesday: '',
      wednesday: '',
      thursday: '',
      friday: '',
      saturday: '',
      sunday: '',
    };

    if (!isWithinProgramDuration) {
      return values;
    }

    const registrationsForWeek = getRegistrationsByWeekNumber(weekNumber);

    Object.entries(registrationsForWeek).forEach(([date, { goal }]) => {
      const formattedDayOfWeek = formatDate(date, 'dddd', 'en');

      values[formattedDayOfWeek] = goal?.toString() ?? '';
    });

    return values;
  }, [getRegistrationsByWeekNumber, isWithinProgramDuration, weekNumber]);

  const handleSubmit = useCallback(async (
    values: FormValues,
    formikHelpers: FormikHelpers<FormValues>,
  ): Promise<void> => {
    formikHelpers.setSubmitting(true);

    try {
      await dispatch(webformSubmit('goal', { ...values, week_number: weekNumber }));
      await dispatch(reinitializeRegistrationData());

      toast.show(t('success_message'), 'success');
      history.push(recoveryPlan);
    } catch (error) {
      formikHelpers.setSubmitting(false);
      toast.show(t('error_message'), 'error');
    }
  }, [dispatch, history, recoveryPlan, t, toast, weekNumber]);

  if (!isWithinProgramDuration) {
    return (
      <p>
        {t('invalid_week')}
      </p>
    );
  }

  return (
    <FormikWrapper
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      <ChangeRecoveryPlanFormInner initialValues={initialValues} />
    </FormikWrapper>
  );
};

const PageChangeRecoveryPlan: React.FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'pages.change_recovery_plan' });
  const history = useHistory();

  const { buildClass, joinClasses } = useClasses('PageChangeRecoveryPlan');
  const { recoveryPlan } = useRoutes();

  return (
    <Modal
      modalVisible
      title={t('modal_title')}
      closeModal={() => history.push(recoveryPlan)}
    >
      <div className={joinClasses(buildClass(), 'col-lg-8', 'offset-lg-2')}>
        <h2 className={buildClass('title')}>
          {t('title')}
        </h2>

        <p className={buildClass('lead')}>
          {t('text')}
        </p>

        <ChangeRecoveryPlanForm />
      </div>
    </Modal>
  );
};

export default PageChangeRecoveryPlan;
